import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import {
  Users,
  FolderGit2,
  LogOut,
  Home,
  Lightbulb,
  CalendarDays,
  BarChart3
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function AdminLayout() {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleLogout = () => {
    logout();
    queryClient.removeQueries();
    navigate("/login");
    apiClient.logout().catch(err => console.error("Background logout error:", err));
  };

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const getItemClass = (path: string) => {
    const active = isActive(path);
    if (active) {
      return "sidebar-item group relative flex items-center justify-center p-2.5 rounded-lg text-primary bg-primary/10 transition-all";
    }
    return "sidebar-item group relative flex items-center justify-center p-2.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-all";
  };

  return (
    <div className="min-h-screen overflow-hidden selection:bg-primary selection:text-primary-foreground relative bg-background text-foreground flex font-ibm">

      {/* Admin Sidebar */}
      <nav
        className="fixed z-50 flex flex-col bg-background/80 w-[72px] border-border border-r pt-6 pb-6 top-0 bottom-0 left-0 backdrop-blur-xl justify-between"
        id="admin-sidebar"
      >
        <div className="flex flex-col items-center gap-6 w-full">
          {/* Sidebar Header: App Logo */}
          <div className="flex items-center justify-center w-full px-2 min-h-[40px] relative">
            <Link
              to="/admin/users"
              className="relative flex items-center justify-center w-10 h-10 shrink-0 rounded-xl cursor-pointer group"
            >
              <div className="w-10 h-10 bg-foreground rounded-xl flex items-center justify-center text-background font-bold font-cabinet text-xl group-hover:bg-primary group-hover:text-primary-foreground transition-colors shadow-lg shadow-black/5">
                A
              </div>
            </Link>
          </div>

          <div className="w-8 h-[1px] bg-border"></div>

          {/* Nav Items */}
          <div className="flex flex-col gap-3 w-full px-2">

            {/* Users */}
            <Link
              to="/admin/users"
              className={getItemClass('/admin/users')}
            >
              <Users className="w-5 h-5 stroke-[1.5]" />
              <span className="absolute left-14 px-2 py-1 bg-foreground text-background font-normal rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50 shadow-lg transition-opacity duration-300 text-xs font-ibm">
                Users
              </span>
            </Link>

            {/* Projects */}
            <Link
              to="/admin/projects"
              className={getItemClass('/admin/projects')}
            >
              <FolderGit2 className="w-5 h-5 stroke-[1.5]" />
              <span className="absolute left-14 px-2 py-1 bg-foreground text-background font-normal rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50 shadow-lg transition-opacity duration-300 text-xs font-ibm">
                Projects
              </span>
            </Link>

            {/* Tactic Management */}
            <Link
              to="/admin/tactics"
              className={getItemClass('/admin/tactics')}
            >
              <Lightbulb className="w-5 h-5 stroke-[1.5]" />
              <span className="absolute left-14 px-2 py-1 bg-foreground text-background font-normal rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50 shadow-lg transition-opacity duration-300 text-xs font-ibm">
                Tactics
              </span>
            </Link>

            {/* Booking Windows */}
            <Link
              to="/admin/booking-windows"
              className={getItemClass('/admin/booking-windows')}
            >
              <CalendarDays className="w-5 h-5 stroke-[1.5]" />
              <span className="absolute left-14 px-2 py-1 bg-foreground text-background font-normal rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50 shadow-lg transition-opacity duration-300 text-xs font-ibm">
                Booking Windows
              </span>
            </Link>

            {/* Ops Dashboard */}
            <Link
              to="/admin/ops-dashboard"
              className={getItemClass('/admin/ops-dashboard')}
            >
              <BarChart3 className="w-5 h-5 stroke-[1.5]" />
              <span className="absolute left-14 px-2 py-1 bg-foreground text-background font-normal rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50 shadow-lg transition-opacity duration-300 text-xs font-ibm">
                Ops Dashboard
              </span>
            </Link>

            {/* Early Access */}
            <Link
              to="/admin/early-access"
              className={getItemClass('/admin/early-access')}
            >
              <Users className="w-5 h-5 stroke-[1.5]" />
              <span className="absolute left-14 px-2 py-1 bg-foreground text-background font-normal rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50 shadow-lg transition-opacity duration-300 text-xs font-ibm">
                Early Access
              </span>
            </Link>

            <div className="w-8 h-[1px] bg-border mx-auto my-1"></div>

            {/* Back to App */}
            <Link
              to="/dashboard"
              className="sidebar-item group relative flex items-center justify-center p-2.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-all"
            >
              <Home className="w-5 h-5 stroke-[1.5]" />
              <span className="absolute left-14 px-2 py-1 bg-foreground text-background font-normal rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-50 shadow-lg transition-opacity duration-300 text-xs font-ibm">
                Back to App
              </span>
            </Link>

          </div>
        </div>

        {/* Bottom Actions */}
        <div className="flex flex-col items-center gap-4 w-full px-2">
          {/* Profile */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="relative mt-2 w-9 h-9 rounded-full overflow-hidden ring-2 ring-destructive/20 hover:ring-destructive/40 transition-all shrink-0 group border border-border">
                <img
                  src={`https://ui-avatars.com/api/?name=${user?.name || 'Admin'}&background=000000&color=fff&bold=true`}
                  alt="Admin Profile"
                  className="w-full h-full object-cover"
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="right" className="w-56 ml-2">
              <DropdownMenuLabel>Admin Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive focus:text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      {/* Content Wrapper (Main) */}
      <div
        id="main-wrapper"
        className="flex flex-1 pl-[72px] transition-all duration-300 h-screen overflow-hidden"
      >
        {/* Zone 2: Main Content Area */}
        <main className="flex-1 min-w-0 flex flex-col z-10 overflow-hidden bg-transparent h-full pt-8 pr-10 pb-8 pl-10 relative overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}